import { action, computed, observable } from 'mobx';

import UserManagementRepository from '@app/src/common/repository/UserManagementRepository';
import notificationService from '@app/src/services/notification-service/NotificationService';
import {
  IMfaChallenge,
  IMfaErrorEnvelope
} from '@app/src/common/model/MfaChallenge';
import {
  IMfaErrorResolution,
  IMfaInlineError,
  MFA_CODE_LENGTH,
  MfaTerminalState,
  formatMinuteSeconds,
  mapMfaError,
  nextExpiresAt,
  resolveMfaInfoMessage,
  resolveSignInOutcome,
  sanitizeCode,
  toOptionalSeconds,
  toValidityMinutes
} from './mfaErrorMapping';

const TICK_INTERVAL = 1000;

export type OnMfaVerified = (
  accessToken: string,
  refreshToken: string
) => Promise<void> | void;

// Local state of an active email two-factor challenge. Kept only for the current page session:
// nothing here is persisted, so a reload returns the user to the credential form.
export default class MfaChallengeStore {
  readonly challengeId: string;
  readonly maskedEmail: string;

  @observable code: string = '';
  @observable verifying: boolean = false;
  @observable resending: boolean = false;
  @observable error: IMfaInlineError | null = null;
  @observable terminal: MfaTerminalState | null = null;
  // False while the challenge has no code at all: the sign-in response reported codeSent = false
  // (a second client signed in inside the send cooldown), or a verification came back as
  // MfaCodeNotSent. Only a successful resend sets it.
  @observable private codeSent: boolean;

  @observable private now: number = Date.now();
  // null when the server reported no usable validity: the local countdown is then not run at all
  // and expiry is left to the server's own outcome.
  @observable private expiresAt: number | null;
  @observable private resendAvailableAt: number;

  // Bumped on every incorrect code so the view can re-select the value even when the same wrong
  // code is submitted twice — the error object alone would be identical and trigger nothing.
  @observable invalidSeq: number = 0;

  // Invalidates in-flight requests whose result must no longer be applied.
  private generation: number = 0;
  private disposed: boolean = false;
  private timer: number | null = null;
  private inputEl: HTMLInputElement | null = null;

  constructor(
    challenge: IMfaChallenge,
    private readonly onVerified: OnMfaVerified
  ) {
    this.challengeId = challenge.challengeId;
    this.maskedEmail = challenge.maskedEmail;
    this.codeSent = challenge.codeSent;
    this.expiresAt =
      challenge.challengeExpiresIn > 0
        ? this.now + challenge.challengeExpiresIn * 1000
        : null;
    // A failed initial delivery makes resend immediately available regardless of the reported wait,
    // otherwise an inconsistent payload could leave the user with no way to recover.
    this.resendAvailableAt = challenge.deliveryFailed
      ? this.now
      : this.now + challenge.resendAfter * 1000;

    if (challenge.deliveryFailed) {
      this.error = { message: 'auth.mfa.error.sendFailed' };
    }

    this.startTimer();
  }

  @computed get hasValidity(): boolean {
    return this.expiresAt !== null;
  }

  @computed get remainingSeconds(): number {
    return this.expiresAt === null
      ? 0
      : Math.max(0, Math.ceil((this.expiresAt - this.now) / 1000));
  }

  @computed get resendSeconds(): number {
    return Math.max(0, Math.ceil((this.resendAvailableAt - this.now) / 1000));
  }

  @computed get validityMinutes(): number {
    return toValidityMinutes(this.remainingSeconds);
  }

  @computed get resendCountdown(): string {
    return formatMinuteSeconds(this.resendSeconds);
  }

  // Requires a code to exist: verifying against a challenge that never had one cannot do anything
  // but spend an attempt. Excludes a pending resend as well: that resend clears the code and
  // supersedes the generation, so a verification racing it would have its "incorrect code" outcome
  // dropped after the server had already spent an attempt on it.
  @computed get canSubmit(): boolean {
    return (
      this.codeSent &&
      this.code.length === MFA_CODE_LENGTH &&
      !this.verifying &&
      !this.resending &&
      !this.terminal
    );
  }

  // A terminal challenge cannot be revived by a new code, so no resend control is offered at all.
  @computed get showResend(): boolean {
    return !this.terminal;
  }

  // Verify must not be marked disabled while it is loading: the shared Button already blocks the
  // click itself, and button_disabled would dim the spinner to 24% opacity.
  @computed get submitDisabled(): boolean {
    return !this.verifying && !this.canSubmit;
  }

  // The link replaces the countdown as soon as the wait elapses; it stays visible while a resend or
  // a verification is running, only inactive, so the row does not flip back to a 0:00 countdown.
  @computed get showResendLink(): boolean {
    return !this.terminal && this.resendSeconds === 0;
  }

  @computed get canResend(): boolean {
    return this.showResendLink && !this.resending && !this.verifying;
  }

  // No code exists for this challenge, so the step must neither claim one was sent nor offer an entry
  // that cannot succeed. A resend is the only way out of it.
  @computed get awaitingCode(): boolean {
    return !this.codeSent;
  }

  // Expiry ends the code's validity, so the sentence stating it is dropped — as is the case when no
  // validity was reported in the first place, and when no code was ever sent.
  @computed get showValidity(): boolean {
    return this.codeSent && this.hasValidity && this.terminal !== 'expired';
  }

  @computed get infoMessage(): string {
    return resolveMfaInfoMessage(this.codeSent, this.showValidity);
  }

  @action
  setCode = (value: string) => {
    if (this.terminal || this.verifying) {
      return;
    }
    this.code = sanitizeCode(value);
    this.error = null;
  };

  setInputEl = (element: HTMLInputElement) => {
    this.inputEl = element;
  };

  selectCode = () => {
    this.inputEl?.select();
  };

  verify = async () => {
    if (!this.canSubmit) {
      return;
    }
    const generation = this.generation;
    this.startVerifying();

    let verified: { accessToken: string; refreshToken: string } | null = null;
    try {
      const response = await UserManagementRepository.verifyMfaCode({
        challengeId: this.challengeId,
        code: this.code
      });
      if (this.disposed) {
        return;
      }
      const outcome = resolveSignInOutcome(response);
      if (outcome.kind !== 'token') {
        this.applyResolution({ notify: 'auth' }, 'verify');
        return;
      }
      verified = {
        accessToken: outcome.accessToken,
        refreshToken: outcome.refreshToken
      };
    } catch (reject: any) {
      if (this.disposed) {
        return;
      }
      // Superseded by a resend or by local expiry: drop the outcome, but never leave the button
      // spinning.
      if (generation !== this.generation) {
        this.stopVerifying();
        return;
      }
      this.applyResolution(toResolution(reject), 'verify');
      return;
    }

    // The challenge is spent and the token is real from here on, so a failure of the sign-in
    // continuation is not a verification failure and must not be reported as one. The continuation
    // has its own recovery (initCurrentUser signs out, checkAccess redirects); all this needs to do
    // is stop presenting the step as busy. `verifying` is otherwise left set: the page navigates.
    try {
      await this.onVerified(verified.accessToken, verified.refreshToken);
    } catch {
      if (!this.disposed) {
        this.stopVerifying();
      }
    }
  };

  resend = async () => {
    if (!this.canResend) {
      return;
    }
    const generation = this.generation;
    this.startResending();
    try {
      const response = await UserManagementRepository.resendMfaCode({
        challengeId: this.challengeId
      });
      if (this.disposed) {
        return;
      }
      if (generation !== this.generation) {
        this.stopResending();
        return;
      }
      this.applyResendSuccess(
        response?.resendAfter,
        response?.challengeExpiresIn
      );
    } catch (reject: any) {
      if (this.disposed) {
        return;
      }
      if (generation !== this.generation) {
        this.stopResending();
        return;
      }
      this.applyResolution(toResolution(reject), 'resend');
    }
  };

  @action
  dispose = () => {
    this.disposed = true;
    this.generation += 1;
    this.stopTimer();
  };

  @action
  private startVerifying() {
    this.verifying = true;
    this.error = null;
  }

  @action
  private startResending() {
    this.resending = true;
  }

  @action
  private stopVerifying() {
    this.verifying = false;
  }

  @action
  private stopResending() {
    this.resending = false;
  }

  @action
  private applyResendSuccess(
    resendAfter?: number,
    challengeExpiresIn?: number
  ) {
    this.resending = false;
    this.generation += 1;
    this.now = Date.now();
    this.code = '';
    this.error = null;
    this.codeSent = true;

    // A new code was issued, so both countdowns restart — but only from timings the response
    // actually carried. Falling back to zero would re-arm resend instantly and, worse, expire the
    // freshly sent code on the very next tick.
    const nextResendAfter = toOptionalSeconds(resendAfter);
    if (nextResendAfter !== undefined) {
      this.resendAvailableAt = this.now + nextResendAfter * 1000;
    }
    this.expiresAt = nextExpiresAt(
      this.expiresAt,
      toOptionalSeconds(challengeExpiresIn),
      this.now
    );
  }

  @action
  private applyResolution(
    resolution: IMfaErrorResolution,
    source: 'verify' | 'resend'
  ) {
    // Clear only this request's own flag: a verify may be running while a resend is still pending.
    if (source === 'verify') {
      this.verifying = false;
    } else {
      this.resending = false;
    }

    if (resolution.notify) {
      // Unrecognised outcome or network failure: keep the current state and reuse the existing
      // login notification.
      notificationService.errorMessage(
        resolution.notify === 'network'
          ? 'notification.network.error'
          : 'notification.auth.error'
      );
      return;
    }

    if (resolution.error) {
      this.error = resolution.error;
    }
    if (resolution.codeNotSent) {
      this.codeSent = false;
      // A resend does not clear the error the way startVerifying does, so a leftover message about
      // a code — a throttle, a failed send — must not survive into a state whose own sentence is
      // that nothing was ever sent.
      this.error = null;
    }
    if (resolution.clearCode) {
      this.code = '';
    }
    if (resolution.terminal) {
      this.setTerminal(resolution.terminal);
    }
    if (resolution.resendAvailable) {
      this.now = Date.now();
      this.resendAvailableAt = this.now;
    }
    if (resolution.resendAfterSeconds !== undefined) {
      this.now = Date.now();
      this.resendAvailableAt = this.now + resolution.resendAfterSeconds * 1000;
    }
    if (resolution.selectCode) {
      this.invalidSeq += 1;
    }
  }

  // First outcome wins, so the attempt-limit message is never replaced once time runs out.
  @action
  private setTerminal(state: MfaTerminalState) {
    if (this.terminal) {
      return;
    }
    this.terminal = state;
    this.generation += 1;
    // Nothing pending can still matter, and a spinner on a dead form would be misleading.
    this.verifying = false;
    this.resending = false;
    this.stopTimer();
  }

  @action
  private tick = () => {
    this.now = Date.now();
    // Only a code that exists can expire. With no code sent, the reported validity describes a
    // challenge whose code was never mailed, and expiring locally would be both untrue and
    // unrecoverable: a terminal state is first-wins and offers no resend, which is the one control
    // this state exists to lead the user to. Expiry is left to the server's own outcome.
    if (
      this.codeSent &&
      this.hasValidity &&
      this.remainingSeconds === 0 &&
      !this.terminal
    ) {
      this.error = { message: 'auth.mfa.error.expired' };
      this.code = '';
      this.setTerminal('expired');
    }
  };

  private startTimer() {
    this.stopTimer();
    this.timer = window.setInterval(this.tick, TICK_INTERVAL);
  }

  private stopTimer() {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}

function toResolution(reject: any): IMfaErrorResolution {
  const response = reject?.response;
  const envelope: IMfaErrorEnvelope | undefined = response?.data;
  return mapMfaError(envelope, !!response);
}
